'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  year: string;
  quarter: string;
  title: string;
  description: string;
  category: 'achievement' | 'expansion' | 'community' | 'recognition';
  icon: string;
}

const timelineEvents: TimelineEvent[] = [
  {
    year: '2016',
    quarter: 'Q1',
    title: 'Academy Foundation',
    description: 'GO Speed Academy officially opened with 15 founding members and a vision to make elite training accessible.',
    category: 'achievement',
    icon: 'FlagIcon'
  },
  {
    year: '2017',
    quarter: 'Q2',
    title: 'First Competition Success',
    description: 'Academy athletes won 12 medals at regional championships, validating our training methodology.',
    category: 'achievement',
    icon: 'TrophyIcon'
  },
  {
    year: '2018',
    quarter: 'Q3',
    title: 'Kids Program Launch',
    description: 'Introduced specialized youth development programs, expanding to serve ages 8-16 with age-appropriate training.',
    category: 'expansion',
    icon: 'UserGroupIcon'
  },
  {
    year: '2019',
    quarter: 'Q1',
    title: 'Facility Expansion',
    description: 'Doubled training space and added state-of-the-art equipment to accommodate growing membership.',
    category: 'expansion',
    icon: 'BuildingOfficeIcon'
  },
  {
    year: '2020',
    quarter: 'Q2',
    title: 'Virtual Training Innovation',
    description: 'Launched online coaching platform during pandemic, maintaining member progress through remote guidance.',
    category: 'expansion',
    icon: 'ComputerDesktopIcon'
  },
  {
    year: '2021',
    quarter: 'Q4',
    title: 'Community Milestone',
    description: 'Reached 500 active members across all programs, establishing GO Speed as regional fitness leader.',
    category: 'community',
    icon: 'HeartIcon'
  },
  {
    year: '2022',
    quarter: 'Q3',
    title: 'Senior Fitness Initiative',
    description: 'Introduced specialized programs for adults 55+, promoting lifelong wellness and mobility.',
    category: 'expansion',
    icon: 'SparklesIcon'
  },
  {
    year: '2023',
    quarter: 'Q1',
    title: 'National Recognition',
    description: 'Featured in national fitness publications for innovative training approach and community impact.',
    category: 'recognition',
    icon: 'StarIcon'
  },
  {
    year: '2024',
    quarter: 'Q2',
    title: 'Certification Partnership',
    description: 'Partnered with international sports science organizations to enhance coach training and member education.',
    category: 'recognition',
    icon: 'AcademicCapIcon'
  },
  {
    year: '2025',
    quarter: 'Q4',
    title: '1000+ Lives Transformed',
    description: 'Celebrated milestone of over 1000 members achieving their fitness goals through structured training.',
    category: 'community',
    icon: 'FireIcon'
  },
  {
    year: '2026',
    quarter: 'Q1',
    title: 'Decade of Excellence',
    description: 'Approaching 10-year anniversary with expanded programs, world-class facilities, and thriving community.',
    category: 'achievement',
    icon: 'BoltIcon'
  }
];

const categoryColors = {
  achievement: 'bg-championship-red/10 text-championship-red border-championship-red/20',
  expansion: 'bg-accent/10 text-accent border-accent/20',
  community: 'bg-success/10 text-success border-success/20',
  recognition: 'bg-gold/10 text-gold border-gold/20'
};

const categoryLabels = {
  achievement: 'Achievement',
  expansion: 'Expansion',
  community: 'Community',
  recognition: 'Recognition'
};

const TimelineSection = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const filteredEvents = selectedCategory === 'all' 
    ? timelineEvents 
    : timelineEvents.filter(event => event.category === selectedCategory);

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-accent/10 rounded-full mb-6">
            <span className="text-accent font-heading font-semibold text-sm">Our Journey</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-primary mb-6 leading-tight">
            Academy Timeline & Milestones
          </h2>
          <p className="text-lg text-text-secondary font-body max-w-3xl mx-auto leading-relaxed mb-8">
            A decade of growth, achievement, and community impact – from 15 founding members to over 1000 lives transformed.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg font-heading font-semibold text-sm transition-all duration-300 ${
                selectedCategory === 'all' ?'bg-primary text-primary-foreground shadow-card' :'bg-card text-text-secondary hover:bg-surface border border-border'
              }`}
            >
              All Events
            </button>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-lg font-heading font-semibold text-sm transition-all duration-300 border ${
                  selectedCategory === key
                    ? categoryColors[key as keyof typeof categoryColors]
                    : 'bg-card text-text-secondary hover:bg-surface border-border'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden lg:block" />

          <div className="space-y-8">
            {filteredEvents.map((event, index) => (
              <div
                key={index}
                className="relative pl-0 lg:pl-20"
              >
                <div className="hidden lg:flex absolute left-0 top-6 w-16 h-16 bg-card rounded-full border-4 border-muted items-center justify-center shadow-card">
                  <Icon name={event.icon as any} size={24} className="text-accent" />
                </div>

                <div className="bg-card rounded-xl p-6 shadow-card hover:shadow-hover transition-all duration-300 border border-border">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex lg:hidden items-center justify-center w-12 h-12 bg-accent/10 rounded-lg">
                        <Icon name={event.icon as any} size={20} className="text-accent" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl font-heading font-bold text-primary">
                            {event.year}
                          </span>
                          <span className="text-sm font-heading font-semibold text-text-secondary">
                            {event.quarter}
                          </span>
                        </div>
                        <h3 className="text-lg font-heading font-bold text-primary">
                          {event.title}
                        </h3>
                      </div>
                    </div>
                    <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-heading font-semibold border ${categoryColors[event.category]}`}>
                      {categoryLabels[event.category]}
                    </div>
                  </div>
                  <p className="text-text-secondary font-body leading-relaxed">
                    {event.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-card rounded-xl p-6 text-center shadow-card border border-border">
            <div className="text-4xl font-heading font-bold text-accent mb-2">10</div>
            <p className="text-text-secondary font-body text-sm">Years of Excellence</p>
          </div>
          <div className="bg-card rounded-xl p-6 text-center shadow-card border border-border">
            <div className="text-4xl font-heading font-bold text-success mb-2">1000+</div>
            <p className="text-text-secondary font-body text-sm">Lives Transformed</p>
          </div>
          <div className="bg-card rounded-xl p-6 text-center shadow-card border border-border">
            <div className="text-4xl font-heading font-bold text-championship-red mb-2">150+</div>
            <p className="text-text-secondary font-body text-sm">Competition Medals</p>
          </div>
          <div className="bg-card rounded-xl p-6 text-center shadow-card border border-border">
            <div className="text-4xl font-heading font-bold text-gold mb-2">25+</div>
            <p className="text-text-secondary font-body text-sm">Community Programs</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;